import { type CSSProperties, useState } from 'react';
import { Drawer } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { NavIcon, useBrandTokens } from './shared';
import type { PatientTab } from './PatientTabsContext';

interface ManagePatientTabsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tabs: PatientTab[];
  activePatientId: string | null;
  pendingTab: PatientTab;
  onConfirm: (closeIds: string[]) => void;
  onCancel: () => void;
}

export function ManagePatientTabsDrawer({
  open,
  onOpenChange,
  tabs,
  activePatientId,
  pendingTab,
  onConfirm,
  onCancel,
}: ManagePatientTabsDrawerProps) {
  const brandTokens = useBrandTokens();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const handleOpenChange = (next: boolean) => {
    if (next) setSelectedIds(new Set());
    else onCancel();
    onOpenChange(next);
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm(Array.from(selectedIds));
    setSelectedIds(new Set());
  };

  return (
    <Drawer
      open={open}
      onOpenChange={handleOpenChange}
      title="Manage Open Patients"
      description={`You've reached the maximum number of open patients. Select patients to close, then confirm to open ${pendingTab.label}.`}
      width={420}
      portalStyle={brandTokens as CSSProperties}
      footer={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Button variant="outline" size="lg" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="gradient"
            size="lg"
            onClick={handleConfirm}
            disabled={selectedIds.size === 0}
          >
            Close {selectedIds.size > 0 ? `(${selectedIds.size})` : ''} & Open
          </Button>
        </div>
      }
    >
      <div
        style={{
          padding: 'var(--sp-6, 24px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-4, 16px)',
        }}
      >
        {/* Pending patient preview */}
        <div
          style={{
            background: 'var(--bg-primary-subtle, #eff6ff)',
            border: '1px solid var(--color-primary-contrast, #93c5fd)',
            borderRadius: 'var(--radius-lg, 14px)',
            padding: '17px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3, 12px)' }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 'var(--radius-full, 9999px)',
                background: pendingTab.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                fontSize: 'var(--font-size-sm, 12px)',
                fontWeight: 'var(--font-weight-semi, 600)',
                color: '#ffffff',
              }}
            >
              {pendingTab.initials}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span
                style={{
                  fontSize: 'var(--font-size-sm, 12px)',
                  fontWeight: 'var(--font-weight-medium, 500)',
                  lineHeight: 'var(--line-height-sm, 16px)',
                  color: 'var(--color-primary, #2563eb)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                }}
              >
                Opening
              </span>
              <span
                style={{
                  fontSize: 'var(--font-size-base, 14px)',
                  fontWeight: 'var(--font-weight-semi, 600)',
                  lineHeight: 'var(--line-height-base, 20px)',
                  color: 'var(--text, #111827)',
                }}
              >
                {pendingTab.label}
              </span>
            </div>
          </div>
        </div>

        {/* Currently open patients */}
        <div
          style={{
            background: 'var(--bg-surface-2, #f9fafb)',
            border: '1px solid var(--border, #e5e7eb)',
            borderRadius: 'var(--radius-lg, 14px)',
            padding: 'var(--sp-5, 20px)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-3, 12px)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span
              style={{
                fontSize: 'var(--font-size-base, 14px)',
                fontWeight: 'var(--font-weight-semi, 600)',
                lineHeight: 'var(--line-height-base, 20px)',
                letterSpacing: '-0.15px',
                color: 'var(--text, #111827)',
              }}
            >
              Currently Open
            </span>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 20,
                height: 20,
                padding: '0 var(--sp-2, 8px)',
                borderRadius: 'var(--radius-sm, 8px)',
                background: 'var(--border, #e5e7eb)',
                fontSize: 'var(--font-size-sm, 12px)',
                fontWeight: 'var(--font-weight-medium, 500)',
                lineHeight: 'var(--line-height-sm, 16px)',
                color: 'var(--text-muted, #6b7280)',
              }}
            >
              {tabs.length}
            </span>
          </div>

          {tabs.map((tab) => {
            const isActive = tab.id === activePatientId;
            const isSelected = selectedIds.has(tab.id);
            return (
              <button
                key={tab.id}
                onClick={() => toggleSelect(tab.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-3, 12px)',
                  padding: '12px',
                  borderRadius: 'var(--radius-md, 10px)',
                  border: isSelected
                    ? '1px solid var(--color-primary, #2563eb)'
                    : '1px solid var(--border, #e5e7eb)',
                  background: isSelected
                    ? 'var(--bg-primary-subtle, #eff6ff)'
                    : 'var(--bg-surface, #ffffff)',
                  cursor: 'pointer',
                  width: '100%',
                  textAlign: 'left',
                  fontFamily: 'inherit',
                }}
              >
                {/* Checkbox */}
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 'var(--radius-xs, 4px)',
                    border: isSelected
                      ? '2px solid var(--color-primary, #2563eb)'
                      : '2px solid var(--border, #e5e7eb)',
                    background: isSelected ? 'var(--color-primary, #2563eb)' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {isSelected && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#ffffff"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>

                {/* Avatar */}
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 'var(--radius-full, 9999px)',
                    background: tab.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    fontSize: 'var(--font-size-sm, 12px)',
                    fontWeight: 'var(--font-weight-semi, 600)',
                    color: '#ffffff',
                  }}
                >
                  {tab.initials}
                </div>

                {/* Name + active badge */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--sp-2, 8px)',
                    flex: 1,
                  }}
                >
                  <span
                    style={{
                      fontSize: 'var(--font-size-base, 14px)',
                      fontWeight: 'var(--font-weight-medium, 500)',
                      lineHeight: 'var(--line-height-base, 20px)',
                      letterSpacing: '-0.15px',
                      color: 'var(--text, #111827)',
                    }}
                  >
                    {tab.label}
                  </span>
                  {isActive && (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        height: 18,
                        padding: '0 6px',
                        borderRadius: 'var(--radius-sm, 8px)',
                        background: 'var(--color-success-bg, #dcfce7)',
                        fontSize: '11px',
                        fontWeight: 'var(--font-weight-medium, 500)',
                        color: 'var(--color-success-text, #166534)',
                      }}
                    >
                      active
                    </span>
                  )}
                </div>

                {/* Close icon hint */}
                {isSelected && <NavIcon name="x" />}
              </button>
            );
          })}
        </div>

        {/* Helper text */}
        <div
          style={{
            background: 'var(--color-info-bg, #eff6ff)',
            border: '1px solid var(--color-info-border, #bfdbfe)',
            borderRadius: 'var(--radius-md, 10px)',
            padding: '12px 17px',
          }}
        >
          <div style={{ display: 'flex', gap: 'var(--sp-3, 12px)' }}>
            <div style={{ color: 'var(--color-info, #3b82f6)', flexShrink: 0, marginTop: 1 }}>
              <NavIcon name="info" />
            </div>
            <span
              style={{
                fontSize: 'var(--font-size-sm, 12px)',
                lineHeight: '19.5px',
                color: '#1e40af',
              }}
            >
              Select one or more patients to close. Their data is saved automatically and you can
              reopen them anytime from the patient list.
            </span>
          </div>
        </div>
      </div>
    </Drawer>
  );
}
